<template>
  <div class="dashboard-grid-container" :class="sizeClass" :style="cssVars">
    <!-- Inline Toolbar (hidden when external toolbar is used) -->
    <DashboardToolbar
      v-if="!hideToolbar"
      :current-size="layout.size"
      :hidden-widgets="hiddenWidgets"
      @size-change="handleSizeChange"
      @add-widget="handleAddWidget"
      @reset-layout="handleResetLayout"
    />

    <!-- ==========================================
         INSIGHTS SECTION (Full-width, prominent)
         ========================================== -->
    <div v-if="showInsightsSection" class="insights-section">
      <div class="insights-header">
        <div class="insights-title-area">
          <i class="fa fa-bolt"></i>
          <div class="insights-title-text">
            <h2 class="insights-title">Intelligent Insights</h2>
            <p class="insights-subtitle">AI-powered alerts and recommendations requiring your attention</p>
          </div>
          <span v-if="insightsCount > 0" class="insights-count-badge">{{ insightsCount }}</span>
        </div>
        <button
          class="insights-hide-btn"
          title="Hide insights"
          @click="handleHideInsights"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="insights-content">
        <ActivityFeed
          ref="activityFeed"
          variant="cards"
          :max-items="6"
          :show-variant-toggle="false"
          :show-header="false"
          @view-all="$emit('view-all-activity')"
          @action="$emit('activity-action', $event)"
        />
      </div>
    </div>

    <!-- ==========================================
         TIER 2: CHARTS ROW (Horizontal drag only)
         ========================================== -->
    <div class="tier-section charts-tier">
      <draggable
        v-model="orderedVisibleCharts"
        :animation="200"
        ghost-class="chart-ghost"
        drag-class="chart-drag"
        class="charts-row"
        :class="{ 'is-dragging': isChartsDragging }"
        @start="isChartsDragging = true"
        @end="handleChartsDragEnd"
      >
        <div
          v-for="chart in orderedVisibleCharts"
          :key="chart.id"
          class="chart-wrapper"
        >
          <!-- Remove Button (top right) -->
          <button
            class="chart-remove-btn"
            title="Hide chart"
            @click.stop="handleRemoveChart(chart.id)"
          >
            <i class="fa fa-times"></i>
          </button>

          <!-- Weekly Activity Chart -->
          <TrendChart
            v-if="chart.id === 'weeklyActivity'"
            title="Weekly Activity"
            subtitle="Tasks and calls completed"
            :data="weeklyActivityData"
            :labels="weeklyLabels"
            :summary-value="totalWeeklyActivity"
            summary-label="Completed this week"
            :summary-trend="weeklyTrend"
            chart-color="#3abf94"
            :inline-stats="activityInlineStats"
            :recent-items="recentActivity"
            recent-title="Recent Activity"
            :view-all-route="{ name: 'tasks' }"
            :show-recent-section="true"
            empty-message="No recent activity"
            default-icon="fa fa-check-circle"
            @period-change="$emit('period-change', $event)"
            @item-click="$emit('activity-item-click', $event)"
          />

          <!-- Appointments Chart -->
          <TrendChart
            v-else-if="chart.id === 'appointmentsChart'"
            title="Appointments"
            subtitle="Scheduled vs completed"
            :data="appointmentsData"
            :labels="weeklyLabels"
            :summary-value="totalAppointments"
            summary-label="This week"
            :summary-trend="appointmentsTrend"
            chart-color="#3b82f6"
            :inline-stats="appointmentsInlineStats"
            :recent-items="recentAppointments"
            recent-title="Upcoming Appointments"
            :view-all-route="{ name: 'scheduling' }"
            :show-recent-section="true"
            empty-message="No upcoming appointments"
            default-icon="fa fa-calendar"
            @period-change="$emit('period-change', $event)"
            @item-click="$emit('appointment-item-click', $event)"
          />

          <!-- Campaigns Chart with embedded details -->
          <template v-else-if="chart.id === 'campaignsChart'">
            <CampaignsTrendChart
              v-if="canShowCampaignsChart"
              :chart-data="campaignsData"
              :labels="weeklyLabels"
              :total-sent="totalCampaignsSent"
              :trend="campaignsTrend"
              :max-campaigns="3"
              :sample-recent-campaigns="sampleRecentCampaigns"
              @period-change="$emit('period-change', $event)"
              @item-click="$emit('campaign-item-click', $event)"
            />
            <!-- Upsell only when the PRACTICE hasn't paid; paid practice + no access → nothing to request. -->
            <div v-else-if="!campaignsPaidFor" class="campaigns-feature-preview" data-test="dashboard-campaigns-chart-upsell">
              <div class="preview-header">
                <div class="preview-title-section">
                  <h3 class="preview-title">Campaigns</h3>
                  <p class="preview-subtitle">Messages sent over time</p>
                </div>
              </div>
              <div class="preview-body">
                <div class="preview-chart-area">
                  <svg viewBox="0 0 400 120" preserveAspectRatio="none" class="placeholder-chart">
                    <defs>
                      <linearGradient id="preview-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stop-color="#8b5cf6" stop-opacity="0.15" />
                        <stop offset="100%" stop-color="#8b5cf6" stop-opacity="0.02" />
                      </linearGradient>
                    </defs>
                    <path d="M 10,100 L 67,80 L 124,90 L 181,60 L 238,70 L 295,40 L 352,50 L 390,30 L 390,100 L 10,100 Z" fill="url(#preview-gradient)" />
                    <path d="M 10,100 L 67,80 L 124,90 L 181,60 L 238,70 L 295,40 L 352,50 L 390,30" fill="none" stroke="#e2e8f0" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <div class="preview-stats">
                  <div class="preview-stat"><span class="stat-value">—</span><span class="stat-label">Active</span></div>
                  <div class="preview-stat"><span class="stat-value">—</span><span class="stat-label">Scheduled</span></div>
                  <div class="preview-stat"><span class="stat-value">—</span><span class="stat-label">Drafts</span></div>
                </div>
              </div>
              <div class="preview-overlay">
                <div class="overlay-content">
                  <i class="fa fa-bullhorn overlay-icon"></i>
                  <strong>Enable Campaigns</strong>
                  <p>Send targeted SMS and email campaigns to patients. Track engagement with built-in analytics.</p>
                  <FeatureRequestButton
                    class="upsell-btn"
                    feature-key="CAMPAIGNS"
                    feature-label="Campaigns"
                    source-page="Dashboard - Campaigns chart"
                  />
                  <FeatureRequestNote feature="CAMPAIGNS" compact />
                </div>
              </div>
            </div>
          </template>

        </div>
      </draggable>
    </div>

    <!-- ==========================================
         TIER 3: WIDGETS GRID (Full 2D drag)
         ========================================== -->
    <div class="tier-section widgets-tier">
      <draggable
        v-model="orderedVisibleWidgets"
        :animation="200"
        ghost-class="widget-ghost"
        drag-class="widget-drag"
        handle=".drag-handle"
        class="widgets-grid customizable"
        @change="handleDragChange"
      >
      <WidgetWrapper
        v-for="widget in orderedVisibleWidgets"
        :key="widget.id"
        :widget-id="widget.id"
        @remove="handleRemoveWidget(widget.id)"
      >
        <!-- My Tasks Today (FREE - always visible) -->
        <MyTasksWidget
          v-if="widget.id === 'myTasks'"
          :max-items="maxItems"
        />

        <!-- Unread Messages (PAID feature - shows FeaturePreview when disabled) -->
        <template v-else-if="widget.id === 'unreadMessages'">
          <UnreadMessagesWidget
            v-if="canShowPaidWidget('unreadMessages')"
            :max-items="maxItems"
          />
          <FeaturePreview
            v-else
            data-test="dashboard-sms-upsell"
            compact
            title="Enable 2-Way SMS Messaging"
            description="Communicate with patients directly via text"
            icon="fa fa-comments"
            feature-key="SMS"
            feature-label="2-Way SMS texting"
            source-page="Dashboard - Unread Messages"
          >
            <template #preview>
              <div class="messages-preview">
                <div class="list-item">
                  <div class="item-avatar" style="background-color: #3b82f6;">JS</div>
                  <div class="item-content">
                    <div class="item-header">
                      <span class="item-title">John Smith</span>
                      <span class="item-time">2 min ago</span>
                    </div>
                    <p class="item-preview">Hi, I need to reschedule my appointment...</p>
                  </div>
                </div>
                <div class="list-item">
                  <div class="item-avatar" style="background-color: #8b5cf6;">MJ</div>
                  <div class="item-content">
                    <div class="item-header">
                      <span class="item-title">Mary Johnson</span>
                      <span class="item-time">15 min ago</span>
                    </div>
                    <p class="item-preview">Thank you! See you tomorrow at 3pm</p>
                  </div>
                </div>
              </div>
            </template>
          </FeaturePreview>
        </template>

        <!-- New Form Submissions (PAID feature - shows FeaturePreview when disabled) -->
        <template v-else-if="widget.id === 'newForms'">
          <NewFormsWidget
            v-if="canShowPaidWidget('newForms')"
            :max-items="maxItems"
          />
          <FeaturePreview
            v-else
            data-test="dashboard-forms-upsell"
            compact
            title="Enable Digital Forms"
            description="Send intake forms to patients digitally"
            icon="fa fa-file-text-o"
            feature-key="FORMS"
            feature-label="Digital Forms"
            source-page="Dashboard - New Form Submissions"
          >
            <template #preview>
              <div class="forms-preview">
                <div class="list-item">
                  <div class="item-avatar form-avatar">
                    <i class="fa fa-file-text-o"></i>
                  </div>
                  <div class="item-content">
                    <div class="item-header">
                      <span class="item-title">Sarah Williams</span>
                      <span class="item-time">5 min ago</span>
                    </div>
                    <p class="item-preview">New Patient Intake Form</p>
                    <span class="item-badge new-patient">Needs Linking</span>
                  </div>
                </div>
                <div class="list-item">
                  <div class="item-avatar form-avatar">
                    <i class="fa fa-file-text-o"></i>
                  </div>
                  <div class="item-content">
                    <div class="item-header">
                      <span class="item-title">Robert Chen</span>
                      <span class="item-time">1 hour ago</span>
                    </div>
                    <p class="item-preview">Medical History Update</p>
                  </div>
                </div>
              </div>
            </template>
          </FeaturePreview>
        </template>

        <!-- Campaigns (PAID feature - shows FeaturePreview when disabled or no permission) -->
        <template v-else-if="widget.id === 'campaigns'">
          <CampaignsWidget
            v-if="canShowWidget('campaigns')"
            :max-items="maxItems"
            :chart-data="campaignsData"
            :total-sent="totalCampaignsSent"
            :trend="campaignsTrend"
          />
          <FeaturePreview
            v-else-if="!campaignsPaidFor"
            data-test="dashboard-campaigns-upsell"
            compact
            title="Enable Campaigns"
            description="Send targeted SMS and email campaigns to patients"
            icon="fa fa-bullhorn"
            feature-key="CAMPAIGNS"
            feature-label="Campaigns"
            source-page="Dashboard - Campaigns widget"
          >
            <template #preview>
              <div class="campaigns-preview">
                <div class="stats-row">
                  <div class="stat-item"><span class="stat-value">0</span><span class="stat-label">Active</span></div>
                  <div class="stat-item"><span class="stat-value">0</span><span class="stat-label">Scheduled</span></div>
                  <div class="stat-item"><span class="stat-value">0</span><span class="stat-label">Drafts</span></div>
                </div>
              </div>
            </template>
          </FeaturePreview>
        </template>

        <!-- Workflow -->
        <WorkflowWidget
          v-else-if="widget.id === 'workflow'"
          :max-items="maxItems"
        />

        <!-- Team Chat -->
        <TeamChatWidget
          v-else-if="widget.id === 'teamChat'"
          :max-items="maxItems"
        />

        <!-- Wiki -->
        <WikiWidget
          v-else-if="widget.id === 'wiki'"
          :max-pinned="Math.min(2, maxItems)"
          :max-recent="maxItems"
        />

        <!-- Kanban -->
        <KanbanWidget
          v-else-if="widget.id === 'kanban'"
          :max-items="maxItems"
        />

        <!-- Note: Insights widget is now shown in its own dedicated section above -->
      </WidgetWrapper>
    </draggable>

    <!-- Empty State for Widgets -->
    <div v-if="orderedVisibleWidgets.length === 0" class="empty-grid-state">
      <i class="fa fa-th-large"></i>
      <p>No widgets visible</p>
      <button class="add-widgets-btn" @click="handleResetLayout">
        <i class="fa fa-plus"></i>
        Restore Default Widgets
      </button>
    </div>
    </div><!-- End widgets-tier -->
  </div>
</template>

<script>
import { debounce } from 'lodash';
import draggable from 'vuedraggable';
import DashboardToolbar from './DashboardToolbar.vue';
import WidgetWrapper from './WidgetWrapper.vue';
import MyTasksWidget from './MyTasksWidget.vue';
import UnreadMessagesWidget from './UnreadMessagesWidget.vue';
import NewFormsWidget from './NewFormsWidget.vue';
import CampaignsWidget from './CampaignsWidget.vue';
import WorkflowWidget from './WorkflowWidget.vue';
import TeamChatWidget from './TeamChatWidget.vue';
import WikiWidget from './WikiWidget.vue';
import KanbanWidget from './KanbanWidget.vue';
import TrendChart from './TrendChart.vue';
import CampaignsTrendChart from './CampaignsTrendChart.vue';
import ActivityFeed from './ActivityFeed.vue';
import FeaturePreview from '@/components/FeaturePreview';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
import {
  WIDGET_REGISTRY,
  CHARTS_REGISTRY,
  SIZE_CONFIG,
  DEFAULT_LAYOUT,
  saveLayout,
  loadLayout,
  getMaxItemsForSize,
  getHiddenWidgets,
  getHiddenCharts,
  getVisibleCharts
} from './dashboardConfig.js';

export default {
  name: 'DashboardGrid',

  components: {
    draggable,
    DashboardToolbar,
    WidgetWrapper,
    MyTasksWidget,
    UnreadMessagesWidget,
    NewFormsWidget,
    CampaignsWidget, // Kept for backwards compatibility, but now in Tier 2
    WorkflowWidget,
    TeamChatWidget,
    WikiWidget,
    KanbanWidget,
    TrendChart,
    CampaignsTrendChart,
    ActivityFeed,
    FeaturePreview,
    FeatureRequestButton,
    FeatureRequestNote
  },

  props: {
    siteId: {
      type: String,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    siteName: {
      type: String,
      default: ''
    },
    messagingEnabled: {
      type: Boolean,
      default: false
    },
    formsEnabled: {
      type: Boolean,
      default: false
    },
    hideToolbar: {
      type: Boolean,
      default: false
    },
    // Chart data props
    weeklyActivityData: {
      type: Array,
      default: () => [12, 19, 15, 25, 22, 30, 28]
    },
    weeklyLabels: {
      type: Array,
      default: () => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    totalWeeklyActivity: {
      type: [String, Number],
      default: 0
    },
    weeklyTrend: {
      type: Number,
      default: null
    },
    appointmentsData: {
      type: Array,
      default: () => [8, 12, 10, 15, 11, 9, 14]
    },
    totalAppointments: {
      type: [String, Number],
      default: 0
    },
    appointmentsTrend: {
      type: Number,
      default: null
    },
    campaignsData: {
      type: Array,
      default: () => [5, 12, 8, 15, 10, 18, 14]
    },
    totalCampaignsSent: {
      type: [String, Number],
      default: 0
    },
    campaignsTrend: {
      type: Number,
      default: null
    },
    // Weekly Activity inline stats and recent items
    activityInlineStats: {
      type: Array,
      default: () => []
    },
    recentActivity: {
      type: Array,
      default: () => []
    },
    // Appointments inline stats and recent items
    appointmentsInlineStats: {
      type: Array,
      default: () => []
    },
    recentAppointments: {
      type: Array,
      default: () => []
    },
    // Campaigns sample recent items (used when no real data available)
    sampleRecentCampaigns: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      // Deep clone to ensure reactivity and avoid mutating the constant
      layout: JSON.parse(JSON.stringify(DEFAULT_LAYOUT)),
      isChartsDragging: false
    };
  },

  computed: {
    sizeClass() {
      return `size-${this.layout.size}`;
    },

    cssVars() {
      const vars = SIZE_CONFIG[this.layout.size]?.vars || SIZE_CONFIG.normal.vars;
      return vars;
    },

    maxItems() {
      return getMaxItemsForSize(this.layout.size);
    },

    visibleWidgets() {
      if (!this.layout.widgets) return [];

      return this.layout.widgets
        .filter(w => {
          if (!w.visible) return false;
          const config = WIDGET_REGISTRY[w.id];
          if (!config) return false;
          // For paid features, always show (will show FeaturePreview if no access)
          // For free features, check permission
          if (config.isPaid) return true; // Always show paid widgets (with FeaturePreview fallback)
          if (config.permission === null) return true;
          return this.hasPermission(config.permission);
        })
        .sort((a, b) => a.order - b.order);
    },

    // Computed property with getter/setter for v-model on draggable
    // Excludes insights since it's now shown in its own dedicated section
    orderedVisibleWidgets: {
      get() {
        // Filter out insights - it's shown in the dedicated section above
        return this.visibleWidgets.filter(w => w.id !== 'insights');
      },
      set(newOrder) {
        // Update the order values in layout.widgets based on new order
        newOrder.forEach((widget, index) => {
          const layoutWidget = this.layout.widgets.find(w => w.id === widget.id);
          if (layoutWidget) {
            layoutWidget.order = index;
          }
        });
        this.persistLayout();
      }
    },

    hiddenWidgets() {
      return getHiddenWidgets(this.layout, this.hasPermission);
    },

    // Hidden charts for "Add Widget" dropdown (Tier 2)
    hiddenCharts() {
      return getHiddenCharts(this.layout, this.hasPermission);
    },

    // Charts tier computed properties
    visibleCharts() {
      return getVisibleCharts(this.layout, this.hasPermission);
    },

    orderedVisibleCharts: {
      get() {
        return this.visibleCharts;
      },
      set(newOrder) {
        // Update the order values in layout.charts based on new order
        newOrder.forEach((chart, index) => {
          const layoutChart = this.layout.charts.find(c => c.id === chart.id);
          if (layoutChart) {
            layoutChart.order = index;
          }
        });
        this.persistLayout();
      }
    },

    // Has the PRACTICE paid for Campaigns? Gates the upsell (ability.js grants view campaigns only when
    // CAMPAIGNS_PAID_FOR is TRUE, but also withholds it from Outsourced — they must not see an upsell).
    // Reads the customer settings ARRAY. The store has no keyed settings object; reading one made this
    // always false, so the upsell showed at paid sites too (prod, 2026-09-16).
    campaignsPaidFor() {
      const settings = (this.$store.state.customer && this.$store.state.customer.settings) || [];
      const row = Array.isArray(settings) ? settings.find((s) => s && s.Property === 'CAMPAIGNS_PAID_FOR') : null;
      return !!(row && String(row.PropertyValue).trim().toUpperCase() === 'TRUE');
    },

    // Check if campaigns chart should show (has permission)
    canShowCampaignsChart() {
      // Check if user has campaigns permission
      return this.hasPermission('campaigns');
    },

    // Check if insights section should be shown (visible in layout)
    showInsightsSection() {
      if (!this.layout.widgets) return false;
      const insightsWidget = this.layout.widgets.find(w => w.id === 'insights');
      return insightsWidget && insightsWidget.visible;
    },

    // Get insights count from ActivityFeed
    insightsCount() {
      if (this.$refs.activityFeed) {
        return this.$refs.activityFeed.allActivities?.length || 0;
      }
      return 0;
    }
  },

  methods: {
    // Check if user has permission to view a widget
    hasPermission(permission) {
      return this.$can('view', permission) || this.$can('view', 'all');
    },

    // Check if a paid feature should be shown
    isPaidFeatureEnabled(widgetId) {
      const config = WIDGET_REGISTRY[widgetId];
      if (!config) return true;
      return true;
    },

    // Combined check: has permission AND (not paid OR paid feature enabled)
    // Works for both widgets and charts
    canShowWidget(widgetId) {
      // Check in WIDGET_REGISTRY first, then CHARTS_REGISTRY
      const config = WIDGET_REGISTRY[widgetId] || CHARTS_REGISTRY[widgetId];
      if (!config) return false;
      // No permission needed
      if (config.permission === null) return true;
      // Check permission
      if (!this.hasPermission(config.permission)) return false;
      return true;
    },

    // Check if a paid widget's underlying feature is enabled
    // This checks actual feature flags (messaging, forms) not just permissions
    canShowPaidWidget(widgetId) {
      switch (widgetId) {
        case 'unreadMessages':
          return this.messagingEnabled;
        case 'newForms':
          return this.formsEnabled;
        case 'campaigns':
          // Campaigns uses permission-based check
          return this.canShowWidget('campaigns');
        default:
          return true;
      }
    },

    handleSizeChange(size) {
      this.layout.size = size;
      this.persistLayout();
    },

    handleAddWidget(widgetId) {
      const existingIndex = this.layout.widgets.findIndex(w => w.id === widgetId);

      if (existingIndex >= 0) {
        // Make it visible again
        this.layout.widgets[existingIndex].visible = true;
        this.layout.widgets[existingIndex].order = this.layout.widgets.length;
      } else {
        // Add new widget
        this.layout.widgets.push({
          id: widgetId,
          visible: true,
          order: this.layout.widgets.length
        });
      }

      this.reorderWidgets();
      this.persistLayout();
    },

    handleRemoveWidget(widgetId) {
      const widget = this.layout.widgets.find(w => w.id === widgetId);
      if (widget) {
        widget.visible = false;
        this.persistLayout();
      }
    },

    handleHideInsights() {
      const insightsWidget = this.layout.widgets.find(w => w.id === 'insights');
      if (insightsWidget) {
        insightsWidget.visible = false;
        this.persistLayout();
      }
    },

    handleRemoveChart(chartId) {
      const chart = this.layout.charts.find(c => c.id === chartId);
      if (chart) {
        chart.visible = false;
        this.persistLayout();
      }
    },

    handleAddChart(chartId) {
      const existingIndex = this.layout.charts.findIndex(c => c.id === chartId);

      if (existingIndex >= 0) {
        // Make it visible again
        this.layout.charts[existingIndex].visible = true;
        this.layout.charts[existingIndex].order = this.layout.charts.length;
      } else {
        // Add new chart
        this.layout.charts.push({
          id: chartId,
          visible: true,
          order: this.layout.charts.length
        });
      }

      // Reorder charts
      this.layout.charts
        .sort((a, b) => a.order - b.order)
        .forEach((c, i) => {
          c.order = i;
        });

      this.persistLayout();
    },

    handleResetLayout() {
      this.layout = JSON.parse(JSON.stringify(DEFAULT_LAYOUT));
      this.persistLayout();
    },

    handleDragChange(event) {
      // vuedraggable handles the reordering via v-model setter
      // This method is called after the drag completes for any additional handling
      if (event.moved) {
        // The orderedVisibleWidgets setter already handles persisting
        // Just log for debugging if needed
        console.log('Widget moved:', event.moved.element.id, 'from', event.moved.oldIndex, 'to', event.moved.newIndex);
      }
    },

    handleChartsDragEnd() {
      this.isChartsDragging = false;
      // The orderedVisibleCharts setter already handles persisting
    },

    reorderWidgets() {
      this.layout.widgets
        .sort((a, b) => a.order - b.order)
        .forEach((w, i) => {
          w.order = i;
        });
    },

    persistLayout: debounce(function() {
      const result = saveLayout(this.siteId, this.userId, this.layout);
      if (!result.success) {
        console.error('Failed to save layout:', result.error);
      }
    }, 300),

    loadSavedLayout() {
      const saved = loadLayout(this.siteId, this.userId);
      if (saved) {
        this.layout = saved;
      }
    }
  },

  created() {
    this.loadSavedLayout();
  }
};
</script>

<style lang="scss" scoped>
// ==========================================
// INSIGHTS SECTION (Full-width, prominent)
// ==========================================
.insights-section {
  margin-bottom: 28px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.04) 0%, rgba(59, 130, 246, 0.02) 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.insights-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(139, 92, 246, 0.1);
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(59, 130, 246, 0.03) 100%);
}

.insights-title-area {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  > i {
    font-size: 1.25rem;
    color: #8b5cf6;
    margin-top: 2px;
  }

  .insights-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 8px;
    background-color: #8b5cf6;
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 12px;
    margin-top: 2px;
  }
}

.insights-title-text {
  .insights-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 4px 0;
  }

  .insights-subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 0;
  }
}

.insights-hide-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background-color: rgba(148, 163, 184, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 0.75rem;
    color: #94a3b8;
  }

  &:hover {
    background-color: #fee2e2;
    i {
      color: #dc2626;
    }
  }
}

.insights-content {
  padding: 0;
  background: white;

  // Override ActivityFeed styles for the full-width display
  :deep(.activity-feed) {
    border: none;
    border-radius: 0;
    box-shadow: none;

    .activity-header {
      padding: 16px 24px;
      border-bottom: 1px solid #e8ecf1;
    }

    .activity-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 0;
      padding: 0;
    }

    .activity-card {
      border-radius: 0;
      border: none;
      border-bottom: 1px solid #e8ecf1;
      border-right: 1px solid #e8ecf1;
      margin: 0;

      &:hover {
        background-color: #fafbfc;
      }
    }
  }
}

// ==========================================
// TIER SECTIONS
// ==========================================
.tier-section {
  margin-bottom: 28px;
}

// Section header - shared between Analytics and Widgets sections
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 0 4px;
}

.section-title-area {
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: #3abf94;
    font-size: 0.875rem;
  }

  .section-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .section-hint {
    font-weight: 400;
    color: #94a3b8;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.75rem;

    &::before {
      content: '•';
      margin: 0 6px;
    }
  }
}

// ==========================================
// TIER 2: CHARTS ROW
// ==========================================
.charts-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  &.is-dragging {
    .chart-wrapper {
      .chart-remove-btn {
        opacity: 1;
      }
    }
  }
}

.chart-wrapper {
  position: relative;
  cursor: grab;
  transition: transform 0.2s ease, opacity 0.2s ease;
  display: flex;
  flex-direction: column;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    .chart-remove-btn {
      opacity: 1;
    }
  }

  // Remove button (top right)
  .chart-remove-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    z-index: 10;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 50%;
    cursor: pointer;
    opacity: 0;
    transition: all 0.15s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    i {
      font-size: 0.75rem;
      color: #94a3b8;
    }

    &:hover {
      background-color: #fef2f2;
      border-color: #fecaca;

      i {
        color: #ef4444;
      }
    }
  }

  // Chart content fills the wrapper
  > .trend-chart-widget,
  > .campaigns-trend-chart,
  > .campaigns-feature-preview {
    width: 100%;
    flex: 1; // Fill the flex container height
  }
}

// Chart drag ghost and active styles
.chart-ghost {
  opacity: 0.4;
  background-color: #e2e8f0;
  border: 2px dashed #94a3b8;
  border-radius: 16px;

  .chart-remove-btn {
    opacity: 0;
  }
}

.chart-drag {
  opacity: 0.9;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: rotate(2deg);
  cursor: grabbing;
}

// ==========================================
// TIER 3: WIDGETS GRID
// ==========================================

.widgets-grid.customizable {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--widget-gap, 20px);
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// Size-specific column layouts
.size-compact {
  // Charts tier - more compact height and spacing
  .charts-row {
    gap: 12px;
  }

  .chart-wrapper {
    // Override chart component internal styles for compact mode
    :deep(.trend-chart-widget),
    :deep(.campaigns-trend-chart) {
      .chart-header {
        padding: 14px 18px 0;
      }

      .chart-title {
        font-size: 0.9375rem;
      }

      .chart-subtitle {
        font-size: 0.75rem;
      }

      .chart-body {
        padding: 12px 18px 12px;
      }

      .summary-value {
        font-size: 1.5rem;
      }

      .summary-label {
        font-size: 0.75rem;
      }

      .inline-stat-value {
        font-size: 1rem;
      }

      .area-chart {
        height: 100px;
      }

      .recent-header,
      .section-header {
        padding: 10px 18px 8px;
      }

      .recent-item,
      .campaign-item {
        padding: 8px 18px;
      }

      .item-avatar,
      .campaign-avatar {
        width: 30px;
        height: 30px;

        i {
          font-size: 0.875rem;
        }
      }

      .item-name,
      .campaign-name {
        font-size: 0.8125rem;
      }

      .item-meta,
      .campaign-meta {
        font-size: 0.6875rem;
      }
    }
  }

  .widgets-grid.customizable {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: 1600px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
}

.size-normal {
  .widgets-grid.customizable {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
}

.size-large {
  .widgets-grid.customizable {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }
}

.empty-grid-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: white;
  border-radius: 12px;
  border: 2px dashed #e2e8f0;
  text-align: center;

  i {
    font-size: 2.5rem;
    color: #cbd5e1;
    margin-bottom: 12px;
  }

  p {
    font-size: 1rem;
    color: #64748b;
    margin: 0 0 16px 0;
  }

  .add-widgets-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #3abf94;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: darken(#3abf94, 8%);
    }
  }
}

// Campaigns preview within FeaturePreview (Tier 3 widget)
:deep(.campaigns-preview) {
  .stats-row {
    display: flex;
    justify-content: space-evenly;
    padding: var(--widget-stat-padding, 12px 20px);
    border-bottom: 1px solid #e8ecf1;
    min-height: 60px;

    .stat-item {
      display: block !important;
      text-align: center;
      flex: 1;
      min-width: 0;
      padding: 0 8px !important;
      border-left: none !important;

      .stat-value {
        display: block;
        font-size: var(--widget-stat-size, 1.5rem);
        font-weight: 700;
        color: #334155;
        line-height: 1.2;
      }

      .stat-label {
        font-size: 0.6875rem;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-top: 2px;
      }
    }
  }
}

// Campaigns Feature Preview (Tier 2 chart) - matches CampaignsTrendChart styling
.campaigns-feature-preview {
  position: relative;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  flex: 1;

  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 20px 24px 0;
  }

  .preview-title-section {
    flex: 1;
  }

  .preview-title {
    font-size: 1.0625rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 4px 0;
  }

  .preview-subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 0;
  }

  .preview-body {
    padding: 16px 24px;
    filter: blur(1px);
    opacity: 0.6;
  }

  .preview-chart-area {
    margin-bottom: 16px;

    .placeholder-chart {
      width: 100%;
      height: 120px;
      display: block;
    }
  }

  .preview-stats {
    display: flex;
    justify-content: flex-end;
    gap: 24px;
  }

  .preview-stat {
    text-align: center;

    .stat-value {
      display: block;
      font-size: 1.25rem;
      font-weight: 700;
      color: #94a3b8;
      line-height: 1.2;
    }

    .stat-label {
      font-size: 0.6875rem;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    background: rgba(255, 255, 255, 0.3);
  }

  .overlay-content {
    text-align: center;
    padding: 20px 24px;
    border-radius: 12px;
    max-width: 280px;
    background-color: white;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border: 1px solid #e2e8f0;

    .overlay-icon {
      font-size: 1.5rem;
      margin-bottom: 8px;
      display: block;
      color: #8b5cf6;
    }

    strong {
      display: block;
      font-size: 0.9375rem;
      font-weight: 600;
      margin-bottom: 4px;
      color: #1e293b;
    }

    p {
      font-size: 0.8125rem;
      margin: 0 0 12px 0;
      color: #64748b;
    }

    .upsell-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 0.8125rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      background-color: #8b5cf6;
      color: white;

      &:hover {
        background-color: #7c3aed;
        text-decoration: none;
        color: white;
      }
    }
  }
}

// Chart preview within FeaturePreview
:deep(.chart-preview) {
  padding: 20px;

  .chart-preview-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 8px;
    color: #94a3b8;

    i {
      font-size: 2rem;
      margin-bottom: 8px;
    }

    span {
      font-size: 0.875rem;
      font-weight: 500;
    }
  }
}

// Draggable ghost and drag styles
.widget-ghost {
  opacity: 0.4;
  background-color: #e2e8f0;
  border: 2px dashed #94a3b8;
  border-radius: 12px;
}

.widget-drag {
  opacity: 0.9;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: rotate(2deg);
  cursor: grabbing;
}
</style>
